/**
 * OfflineBanner Component
 *
 * Component that displays a banner when the app is offline.
 * Shows helpful message and provides retry functionality.
 */

import { useEffect, useState, useCallback } from 'react'
import { AlertTriangle } from 'lucide-react'
import { isOnline } from '@/lib/pwa'
import { useT } from '@/contexts/I18nContext'

interface OfflineBannerProps {
  onRetry?: () => void
}

export function OfflineBanner({ onRetry }: OfflineBannerProps) {
  const [online, setOnline] = useState(() => isOnline())
  const t = useT()

  useEffect(() => {
    const handleOnline = () => setOnline(true)
    const handleOffline = () => setOnline(false)

    // Listen for online/offline events
    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)

    // Cleanup
    return () => {
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
    }
  }, [])

  const handleRetry = useCallback(() => {
    if (onRetry) {
      // Use custom retry handler
      onRetry()
    } else {
      // Default behavior: reload the page
      window.location.reload()
    }
  }, [onRetry])

  // Don't render if online
  if (online) {
    return null
  }

  return (
    <div
      role="banner"
      aria-live="polite"
      aria-label={t('offline.notification')}
      className="fixed top-0 left-0 right-0 bg-amber-500 text-white px-4 py-3 z-50 shadow-md animate-slide-down"
    >
      <div className="max-w-7xl mx-auto flex items-center justify-between gap-4">
        {/* Message */}
        <div className="flex items-center gap-3 flex-1">
          {/* Warning Icon */}
          <AlertTriangle className="w-5 h-5 shrink-0" aria-hidden="true" />

          {/* Text */}
          <div className="flex-1">
            <p className="font-medium text-sm">
              {t('offline.title')}
            </p>
            <p className="text-xs text-amber-100 mt-0.5">
              {t('offline.description')}
            </p>
          </div>
        </div>

        {/* Retry Button */}
        <button
          onClick={handleRetry}
          className="flex-shrink-0 bg-white text-amber-600 hover:bg-amber-50 px-4 py-2 rounded-lg text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-amber-500"
          aria-label={t('offline.retryConnection')}
        >
          {t('offline.retryConnection')}
        </button>
      </div>
    </div>
  )
}
